import { createIcon } from "@/components/icons/createIcon";
import { collapseLabelText } from "@/components/tiptap/collapse/utils";
import {
  CORCA_TOOLTIP_TRIGGER_CLASS,
  tooltipBoxSpec,
} from "@/components/tiptap/utils/specs/tooltip";
import { twMerge } from "@/utils/twMerge";

import { button } from "./button";
import { replaceContentHole } from "./utils";

import type { Translator } from "@/components/tiptap/EditorTranslator";
import type { DOMOutputSpec } from "@tiptap/pm/model";

type ToggleButtonOptions = {
  nodeTypeName?: string;
  className?: string;
  icon?: SVGSVGElement | null;
  t?: Translator;
};

export function toggleButtonSpec(
  collapsed: boolean,
  { nodeTypeName = "block", className, icon, t }: ToggleButtonOptions = {},
): DOMOutputSpec {
  const labelText = collapseLabelText(collapsed, nodeTypeName, t);

  return button(
    {
      class: twMerge(
        "w-5 h-5 z-10",
        "hoverable-icon-main",
        CORCA_TOOLTIP_TRIGGER_CLASS,
        collapsed
          ? "collapse-expand [--color-icon-main:var(--color-icon-active)]"
          : "[--color-icon-main:var(--color-line-number-text)]",
        className,
      ),
      "data-collapse-toggle": "",
      "aria-label": labelText,
      "aria-expanded": collapsed ? "false" : "true",
    },
    icon ??
      createIcon(collapsed ? "Plus" : "Minus", {
        "aria-hidden": "true",
        className: "w-6 flex-shrink-0",
      }) ??
      (collapsed ? "+" : "-"),
    tooltipBoxSpec(labelText, { attrs: { "aria-hidden": "true" } }),
  );
}

type ToggleSectionButtonOptions = {
  nodeTypeName?: string;
  className?: string;
  t?: Translator;
};

export function toggleSectionButtonSpec(
  collapsed: boolean,
  { nodeTypeName, className, t }: ToggleSectionButtonOptions = {},
): DOMOutputSpec {
  return toggleButtonSpec(collapsed, {
    nodeTypeName,
    className,
    icon: createIcon("ChevronDown", {
      "aria-hidden": "true",
      class: twMerge("w-6 flex-shrink-0", collapsed && "-rotate-90"),
    }),
    t,
  });
}

export function collapsedNumberSlotSpec(nodeTypeName?: string, t?: Translator): DOMOutputSpec {
  return [
    "div",
    {
      class: "flex items-center shrink-0 select-none",
    },
    toggleButtonSpec(true, { nodeTypeName, t }),
  ];
}

export const collapseRuleSpec: DOMOutputSpec = [
  "div",
  {
    class: twMerge("collapse-rule flex-1 h-0.5 rounded-r bg-transparent transition-colors"),
  },
];

export function togglePillSpec(
  content: DOMOutputSpec,
  collapsed: boolean,
  { nodeTypeName, t }: ToggleSectionButtonOptions = {},
): DOMOutputSpec {
  return replaceContentHole(content, [
    "span",
    {
      class: twMerge(
        "group/collapse-pill",
        collapsed &&
          "collapse-pill rounded-6 fromSm:pl-0.5 fromSm:pr-1 fromSm:-ml-0.5 cursor-pointer transition-colors fromSm:hover:bg-sidebar-button-active-bg has-[[data-collapse-toggle]:hover]:bg-transparent",
      ),
    },
    toggleSectionButtonSpec(collapsed, {
      nodeTypeName,
      className: twMerge(
        "inline-flex align-middle -translate-y-[0.1em] mr-0.5 h-6 [--color-icon-main:var(--color-document-text)] sm:hidden",
        collapsed && "group-hover/collapse-pill:[--color-icon-main:var(--color-icon-active)]",
      ),
      t,
    }),
    // The content hole must be the only child of its parent, so the title gets its own span.
    ["span", {}, 0],
  ]);
}
